import React, { useEffect, useRef, useState } from "react";
import { api } from "./api";
import { SlopResult, SlopApiError, Dimension } from "./types";

// Flip to true later to blur the detailed breakdown behind an email capture.
const LEAD_GATE_ENABLED = false;

const CTA_URL = "https://core6.marketing/voice-of-customer-content-marketing/";

// Pre-graded example shown via the "see a sample report" link (stays cached server-side).
const SAMPLE_URL = "https://palaciosconstructionca.com/garage-conversion-adu-monterey-county-what-to-expect/";

const BAND_COLORS: Record<string, string> = {
  "Human-crafted": "#1D9E75",
  "Strong": "#1D9E75",
  "Mixed": "#BA7517",
  "Heavy AI slop signals": "#D85A30",
  "Pure slop": "#E24B4A",
};

function bandColor(band: string): string {
  return BAND_COLORS[band] || "#BA7517";
}

const BAND_CONSEQUENCE: Record<string, string> = {
  "Human-crafted":
    "Content at this level is exactly what Google's helpful-content systems reward — and it gives AI platforms something genuinely worth citing.",
  "Strong":
    "At this level Google has real reasons to prefer your page, and AI platforms have distinctive material to cite. The goal now is keeping every page here.",
  "Mixed":
    "At this level Google will index the page but has little reason to prefer it — and AI platforms have nothing distinctive to cite.",
  "Heavy AI slop signals":
    "Content like this is what Google's scaled-content policies were written about — it rarely earns rankings, and AI platforms will never cite it.",
  "Pure slop":
    "This is the content Google's spam policies explicitly target. Left in place, pages like this can drag down how Google sees the whole site.",
};

const LOADING_MESSAGES = [
  "Reading the page…",
  "Extracting the article…",
  "Grading the content…",
  "Scoring the six dimensions…",
  "Writing your top fixes…",
];

function track(event: string, params?: Record<string, string>) {
  const fbq = (window as any).fbq;
  if (typeof fbq === "function") fbq("trackCustom", event, params || {});
}

type Phase = "idle" | "loading" | "results";

export default function SlopPage() {
  const [url, setUrl] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState("");
  const [result, setResult] = useState<SlopResult | null>(null);

  async function runCheck(target: string, source: "user" | "sample" = "user") {
    if (!target.trim() || phase === "loading") return;
    setError("");
    setResult(null);
    setPhase("loading");
    track("SlopCheckRun", { source });

    try {
      const data = await api.analyzeContent(target.trim());
      setResult(data);
      setPhase("results");
    } catch (err: any) {
      if (err instanceof SlopApiError) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
      setPhase("idle");
    }
  }

  function handleCheck(e: React.FormEvent) {
    e.preventDefault();
    runCheck(url);
  }

  function handleSample(e: React.MouseEvent) {
    e.preventDefault();
    setUrl(SAMPLE_URL);
    runCheck(SAMPLE_URL, "sample");
  }

  useEffect(() => {
    if (phase === "results" && result) {
      track("SlopResultShown", { band: result.band });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [phase, result]);

  const showEducation = phase === "idle" && !result;

  return (
    <div className="page-wrap">
      <div className="trust-bar">
        Veteran-Owned&nbsp;&nbsp;|&nbsp;&nbsp;BBB Accredited&nbsp;&nbsp;|&nbsp;&nbsp;20+ Years on the Central Coast
      </div>
      <div className="hero">
        <div className="hero-logo">
          <a href="https://core6.marketing" target="_blank" rel="noopener noreferrer">
            <img src="/core6-logo.png" alt="Core6 Marketing" />
          </a>
        </div>
        <div className="hero-eyebrow">Free Content Quality Check</div>
        <h1>Is Your Content <span>Human-Crafted</span><br />— or AI Slop?</h1>
        <p>
          Paste the URL of a blog post or article. We grade its content quality on six editorial
          dimensions and tell you whether it reads human-crafted — or like AI slop.
        </p>

        <form className="audit-form" onSubmit={handleCheck}>
          <input
            type="text"
            placeholder="https://yoursite.com/blog/your-best-post"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={phase === "loading"}
          />
          <button className="btn-primary" type="submit" disabled={phase === "loading" || !url.trim()}>
            {phase === "loading" ? "Checking…" : result ? "Check another page" : "Check for AI slop"}
          </button>
        </form>

        <div className="slop-trust-line">
          Graded by AI reading your page like a senior editor — not a keyword counter, not an "AI detector."
        </div>
        {showEducation && (
          <div className="slop-hero-links">
            <a href="#sample" onClick={handleSample}>See a sample report →</a>
            <span className="slop-hero-sep">·</span>
            <a href="#learn">What is AI slop? ↓</a>
          </div>
        )}

        {error && (
          <div className="error-box" style={{ marginTop: 16, maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}>
            {error}
          </div>
        )}
      </div>

      <div className="content">
        {phase === "loading" && <LoadingState />}
        {phase === "results" && result && <Results result={result} />}
        {showEducation && <Education />}
      </div>

      <div className="footer">
        <a href="https://core6.marketing" target="_blank" rel="noopener noreferrer">core6.marketing</a>
        {" · "}
        <a href="tel:8317899320">(831) 789-9320</a>
        {" · "}
        <a href="/admin">Admin</a>
      </div>
    </div>
  );
}

// ── Education (shown before a check is run) ──────────────────────────────────

const DIMENSION_GUIDE = [
  { name: "Expert voice", weight: "20%", blurb: "Explains why, when, and which — not textbook facts anyone could look up." },
  { name: "Experience", weight: "20%", blurb: "First-hand, done-the-work signals. “The question I hear most” beats neutral summary." },
  { name: "Voice of customer", weight: "15%", blurb: "Built around real pre-purchase questions, not keyword lists." },
  { name: "Originality", weight: "15%", blurb: "Angles competitors skip — not the same consensus content found everywhere." },
  { name: "Specificity & evidence", weight: "15%", blurb: "Names, numbers, dates, local detail — concrete beats abstract." },
  { name: "Craft & freshness", weight: "15%", blurb: "Varied rhythm, real opinions, none of the “in today's fast-paced world” tells." },
];

const SCORE_GUIDE = [
  { range: "85–100", label: "Human-crafted", color: "#1D9E75", blurb: "Rare air — genuine expertise on the page. Almost nobody starts here." },
  { range: "70–84", label: "Strong", color: "#1D9E75", blurb: "The realistic target. Content that earns rankings and citations." },
  { range: "50–69", label: "Mixed", color: "#BA7517", blurb: "Typical of an AI draft with light editing. Won't hurt you badly, won't get cited." },
  { range: "0–49", label: "The slop zone", color: "#D85A30", blurb: "The content Google's spam policies were written about." },
];

function Education() {
  return (
    <div id="learn">
      <div className="card">
        <h3 className="slop-section-title">What is AI slop?</h3>
        <p className="slop-learn-text">
          AI slop is content that could have been written about any business, by anyone — generic
          advice, padded filler, no first-hand experience, nothing a reader couldn't find on a
          thousand other pages. AI usually wrote it, but that's not the crime; <strong>publishing it
          unedited is.</strong> Well-edited, AI-assisted content built around real expertise scores
          well here — and generic content scores low even when a human wrote every word. This isn't
          an AI detector. It's a quality grade.
        </p>
      </div>

      <div className="card">
        <h3 className="slop-section-title">Why it matters</h3>
        <ul className="slop-why">
          <li>
            <strong>Google is actively hunting this content.</strong> Its helpful-content systems and
            scaled-content-abuse policy target mass-produced generic pages — and thin pages can drag
            down how Google sees your <em>whole site</em>, not just one URL.
          </li>
          <li>
            <strong>AI platforms only cite pages worth citing.</strong> ChatGPT, Perplexity, and Google
            AI Overviews pull from pages with original details, real numbers, and first-hand
            experience. A page that restates what every competitor says gives them no reason to
            point at you.
          </li>
          <li>
            <strong>Experience is now the first thing Google grades.</strong> Its quality guidelines
            lead with the "E" for Experience (E-E-A-T) — content that shows you've actually done the
            work is exactly what gets rewarded.
          </li>
        </ul>
      </div>

      <div className="card">
        <h3 className="slop-section-title">What we grade — six dimensions</h3>
        <div className="slop-dims-grid">
          {DIMENSION_GUIDE.map((d) => (
            <div key={d.name} className="slop-dims-cell">
              <div className="slop-dims-name">{d.name} <span>({d.weight})</span></div>
              <div className="slop-dims-blurb">{d.blurb}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 className="slop-section-title">What's a good score?</h3>
        {SCORE_GUIDE.map((b) => (
          <div key={b.label} className="slop-band-row">
            <span className="slop-band-dot" style={{ background: b.color }} />
            <span className="slop-band-range">{b.range}</span>
            <span className="slop-band-label" style={{ color: b.color }}>{b.label}</span>
            <span className="slop-band-blurb">{b.blurb}</span>
          </div>
        ))}
        <p className="slop-learn-text" style={{ marginTop: 18 }}>
          Most local business blog posts we test score <strong>between 30 and 50</strong>. A 100 isn't
          the goal — distinctiveness is. Score 70+ and you're ahead of nearly every competitor in
          your market.
        </p>
      </div>
    </div>
  );
}

// ── Loading ───────────────────────────────────────────────────────────────────

function LoadingState() {
  const [msgIndex, setMsgIndex] = useState(0);
  const [progress, setProgress] = useState(4);
  const progressRef = useRef(4);

  useEffect(() => {
    const msgTimer = setInterval(() => {
      setMsgIndex((i) => Math.min(i + 1, LOADING_MESSAGES.length - 1));
    }, 5000);
    // Creep toward 92% — grading takes 10–30s, never look frozen.
    const barTimer = setInterval(() => {
      progressRef.current = Math.min(92, progressRef.current + (92 - progressRef.current) * 0.045);
      setProgress(progressRef.current);
    }, 300);
    return () => { clearInterval(msgTimer); clearInterval(barTimer); };
  }, []);

  return (
    <div className="card loading-state">
      <div className="spinner" />
      <h3>{LOADING_MESSAGES[msgIndex]}</h3>
      <p>Full editorial grading takes 10–30 seconds.</p>
      <div className="slop-progress-track">
        <div className="slop-progress-fill" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

// ── Results ───────────────────────────────────────────────────────────────────

function Results({ result }: { result: SlopResult }) {
  const color = bandColor(result.band);
  const strong = result.overallScore >= 70;

  return (
    <>
      {/* 1. Analyzed URL */}
      <div className="slop-url">{result.url}</div>

      {/* 2. Score ring + verdict */}
      <div className="card">
        <div className="score-section">
          <ScoreRing score={result.overallScore} color={color} />
          <div className="score-info">
            <span className="score-band" style={{ background: `${color}1A`, color }}>
              {result.band}
            </span>
            <h2 style={{ fontSize: 19, lineHeight: 1.35 }}>{result.aiFeelVerdict}</h2>
            <p>{result.summary}</p>
          </div>
        </div>
        <CalibrationStrip score={result.overallScore} />
        <p className="slop-consequence" style={{ borderLeftColor: color }}>
          {BAND_CONSEQUENCE[result.band]}
        </p>
      </div>

      <GatedSection locked={LEAD_GATE_ENABLED}>
        {/* 3. Score breakdown */}
        <div className="card categories">
          <h3>Score Breakdown</h3>
          {result.dimensions.map((d) => (
            <DimensionRow key={d.name} dimension={d} color={color} />
          ))}
        </div>

        {/* 4. Content level */}
        <div className="card">
          <h3 className="slop-section-title">Content Maturity Level</h3>
          <div className="slop-levels">
            {[
              { n: 1, label: "Prompt & publish" },
              { n: 2, label: "Prompt & edit" },
              { n: 3, label: "Knowledge base" },
            ].map(({ n, label }) => {
              const active = result.contentLevel === n;
              return (
                <div
                  key={n}
                  className={`slop-level-cell${active ? " active" : ""}`}
                  style={active ? { borderColor: color, background: `${color}14` } : undefined}
                >
                  <div className="slop-level-num" style={active ? { color } : undefined}>Level {n}</div>
                  <div className="slop-level-label">{result.contentLevel === n ? result.contentLevelLabel || label : label}</div>
                </div>
              );
            })}
          </div>
          <p className="slop-level-explain">{result.contentLevelExplanation}</p>
        </div>

        {/* 5. Top fixes */}
        <div className="card">
          <h3 className="slop-section-title">Top Fixes</h3>
          <ul className="slop-fixes">
            {result.topFixes.map((fix, i) => (
              <li key={i}>
                <span className="slop-fix-arrow">→</span>
                <span>{fix}</span>
              </li>
            ))}
          </ul>
        </div>
      </GatedSection>

      {/* 6. CTA */}
      <div className="slop-cta">
        <h3>{strong ? "Your content is already strong." : "This is fixable."}</h3>
        <p>
          {strong
            ? "Want to keep it here across every page? Core6 builds Level 3 content systems that scale this quality across your whole site."
            : "Core6's content process turns pages like this into content that earns trust and gets cited by AI."}
        </p>
        <a
          className="slop-cta-btn"
          href={CTA_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("SlopCTAClick", { band: result.band })}
        >
          Get a free content strategy call
        </a>
      </div>
    </>
  );
}

/** Horizontal band scale with a marker at the page's score. */
function CalibrationStrip({ score }: { score: number }) {
  const segments = [
    { to: 30, color: "#E24B4A" },
    { to: 50, color: "#D85A30" },
    { to: 70, color: "#BA7517" },
    { to: 85, color: "#1D9E75" },
    { to: 100, color: "#157A5B" },
  ];
  let from = 0;
  return (
    <div className="slop-cal">
      <div className="slop-cal-track">
        {segments.map((s) => {
          const width = s.to - from;
          const el = (
            <div key={s.to} className="slop-cal-seg" style={{ width: `${width}%`, background: s.color }} />
          );
          from = s.to;
          return el;
        })}
        <div className="slop-cal-marker" style={{ left: `${score}%` }}>
          <div className="slop-cal-marker-line" />
          <div className="slop-cal-marker-label">you</div>
        </div>
      </div>
      <div className="slop-cal-caption">
        Most pages we test score 30–50 · 70+ puts you ahead of nearly every competitor
      </div>
    </div>
  );
}

function ScoreRing({ score, color }: { score: number; color: string }) {
  const r = 56;
  const c = 2 * Math.PI * r;
  const filled = (score / 100) * c;
  return (
    <div className="slop-ring">
      <svg width="140" height="140" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={r} fill="none" stroke="#E8E1D8" strokeWidth="9" />
        <circle
          cx="70" cy="70" r={r} fill="none"
          stroke={color} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${filled} ${c - filled}`}
          transform="rotate(-90 70 70)"
          style={{ transition: "stroke-dasharray 1s ease" }}
        />
      </svg>
      <div className="slop-ring-text">
        <span className="slop-ring-score" style={{ color }}>{score}</span>
        <span className="slop-ring-denom">/ 100</span>
      </div>
    </div>
  );
}

function DimensionRow({ dimension, color }: { dimension: Dimension; color: string }) {
  return (
    <div className="category-row" style={{ marginBottom: 22 }}>
      <div className="category-header">
        <span className="category-name">{dimension.name}</span>
        <span className="category-score" style={{ color }}>{dimension.score}/100</span>
      </div>
      <div className="bar-track">
        <div className="bar-fill" style={{ width: `${dimension.score}%`, background: color }} />
      </div>
      <div className="slop-dim-rationale">
        {dimension.rationale}
        {dimension.evidence.length > 0 && (
          <span className="slop-dim-evidence"> “{dimension.evidence[0]}”</span>
        )}
      </div>
    </div>
  );
}

/**
 * Lead-gate scaffold: when locked, the detailed sections blur behind an
 * email-capture overlay. Off by default — flip LEAD_GATE_ENABLED to enable.
 */
function GatedSection({ locked, children }: { locked: boolean; children: React.ReactNode }) {
  if (!locked) return <>{children}</>;
  return (
    <div className="slop-gate-wrap">
      <div className="slop-gate-blur">{children}</div>
      <div className="slop-gate-overlay">
        <div className="slop-gate-box">
          <h3>Unlock the full breakdown</h3>
          <p>Enter your email to see all six dimension scores, your content level, and the top fixes.</p>
          {/* Email capture form goes here when the gate is enabled */}
        </div>
      </div>
    </div>
  );
}
